import { defaultsDeep, isNil } from 'lodash-es'
import {
  any,
  array,
  type BaseIssue,
  type BaseSchema,
  boolean,
  check,
  date,
  description,
  fallback,
  file,
  forward,
  type GenericSchema,
  getDefault,
  getFallbacks,
  getMetadata,
  type InferOutput,
  integer,
  isoDate,
  type LengthInput,
  literal,
  maxLength,
  maxValue,
  message,
  metadata,
  minLength,
  minValue,
  nullish,
  number,
  type ObjectEntries,
  object,
  omit,
  optional,
  type PicklistOptions,
  partial,
  pick,
  picklist,
  pipe,
  rawCheck,
  regex,
  required,
  strictObject,
  string,
  transform,
  undefinedable,
  union,
  type ValueInput,
  variant,
} from 'valibot'

import { fvDateUtc } from '@fv/dates'
import {
  allowEmptyStringRegex,
  domainRegex,
  emailRegex,
  normalizeEmail,
} from '@fv/models/core'

export const vObject = <T extends ObjectEntries>(
  entries: T,
  errorMessage?: string,
) => object(entries, errorMessage)

export const vStrictObject = <T extends ObjectEntries>(
  entries: T,
  errorMessage = 'Not a valid property',
) => strictObject(entries, errorMessage)

export const vAny = any
export const vArray = array
export const vPipe = pipe
export const vMessage = message
export const vCheck = check
export const vTransform = transform
export const vOmit = omit
export const vPick = pick
export const vOneOf = union
export const vRequired = required
export const vLiteral = literal
export const vUnion = union
export const vPartial = partial
export const vVariant = variant
export const vRawCheck = rawCheck
export const vBoolean = () =>
  pipe(
    union([boolean(), picklist(['true', 'false'])]),
    transform(v => (typeof v === 'boolean' ? v : v === 'true')),
  )

export const vBoolStrict = () => boolean()

export const vDefaults = <TSchema extends GenericSchema>(
  schema: TSchema,
  defaults: Partial<InferOutput<TSchema>>,
) =>
  pipe(
    schema,
    transform(v => defaultsDeep(v, defaults)),
  )

export const vString = (errorMessage?: string) =>
  pipe(
    string(errorMessage),
    transform(v => {
      return isNil(v) ? v : String(v)
    }),
  )

export const vObjectId = () => pipe(vString(), minLength(24), maxLength(24))

const loadRefMetadata = { fvRef: 'load' } as const

/** Marks a schema entry as a load id so route middleware can enforce ownership without matching on the field name. */
export const asLoadRef = <TSchema extends GenericSchema>(schema: TSchema) =>
  pipe(schema, metadata(loadRefMetadata))

export const vLoadRef = () => asLoadRef(vObjectId())

export const isLoadRef = (schema: GenericSchema) =>
  (getMetadata(schema) as Partial<typeof loadRefMetadata>).fvRef ===
  loadRefMetadata.fvRef

export const vPicklist = <const TOptions extends PicklistOptions>(
  options: TOptions,
  errorMessage?: string,
) => picklist(options, errorMessage || `Must be one of ${options.join(', ')}`)

export const vCoerceArray = <T extends GenericSchema>(schema: T) =>
  union([
    pipe(
      schema,
      transform(v => [v]),
    ),
    array(schema),
  ])

export const vAddFields = <
  TOutput extends Record<string, unknown>,
  T extends BaseSchema<unknown, TOutput, BaseIssue<unknown>>,
  TFields,
>(
  schema: T,
  fields: TFields,
) => {
  return pipe(
    schema,
    transform(v => ({ ...v, ...fields })),
  )
}

export const vDomain = pipe(
  string(),
  regex(domainRegex),
  transform(v => (v ? v.toLowerCase().trim() : v)),
)

export const vEmail = pipe(
  string(),
  regex(emailRegex),
  transform(v => (v ? normalizeEmail(v) : v)),
)

export const vEmailOrEmpty = pipe(
  string(),
  regex(allowEmptyStringRegex(true, emailRegex)),
  transform(v => (v ? v.toLowerCase().trim() : v)),
)

export const vNumber = () =>
  pipe(
    number(),
    transform(v => (v ? Number(v) : v)),
  )

export const vOpt = optional
export const vUndef = undefinedable

export const vOptional = <TSchema extends GenericSchema>(
  wrapped: TSchema,
  defaultValue?: InferOutput<TSchema>,
) => nullish(wrapped, defaultValue)

export const vOptionalDef = <
  TSchema extends GenericSchema,
  TDefault extends InferOutput<TSchema>,
>(
  wrapped: TSchema,
  def: TDefault,
) => nullish(wrapped, def) as unknown as TSchema //  https://github.com/fabian-hiller/valibot/issues/1180 - Appears to be an issue distinguishing between null and undefined with `strictNullChecks=false`

export const vDescription = description
export const vMetadata = metadata
export const vFallback = fallback
export const createDefault = getFallbacks
export const vFallbackObj = <const TSchema extends GenericSchema>(
  wrapped: TSchema,
) => vFallback(wrapped, createDefault(wrapped))

export const vMaxLength = <
  TInput extends LengthInput,
  const TRequirement extends number,
>(
  value: TRequirement,
) =>
  maxLength<TInput, TRequirement, string>(
    value,
    `Must have length less than or equal to ${value.toString()}.`,
  )

export const vMinLength = <
  TInput extends LengthInput,
  const TRequirement extends number,
>(
  value: TRequirement,
  message?: string,
) =>
  minLength<TInput, TRequirement, string>(
    value,
    message || `Must have length greater than or equal to ${value.toString()}.`,
  )

export const vMinValue = <
  TInput extends ValueInput,
  const TRequirement extends TInput,
>(
  value: TRequirement,
  message?: string,
) =>
  minValue<TInput, TRequirement, string>(
    value,
    message || `Must be greater than or equal to ${value.toString()}.`,
  )

export const vMaxValue = <
  TInput extends ValueInput,
  const TRequirement extends TInput,
>(
  value: TRequirement,
) =>
  maxValue<TInput, TRequirement, string>(
    value,
    `Must be less than or equal to ${value.toString()}.`,
  )

export const vRegex = (r: RegExp, errorMessage?: string) =>
  regex(r, errorMessage || 'Invalid value provided')

export const vInt = integer
export const vISODate = isoDate
export const vISODateTime = () =>
  regex(
    /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/,
  )

export const vNonEmptyString = () => pipe(string(), vMinLength(1))
export const vFile = () => file()
export const vDate = () =>
  pipe(
    union([string(), date()]),
    transform(v => {
      if (!v) {
        return null
      }

      return fvDateUtc(v as string).toDate()
    }),
  )

export const vStringToNumber = () =>
  vPipe(
    vUnion([vString(), vNumber()]),
    vTransform(v => Number(v)),
  )

export const vNumberToString = () =>
  vPipe(
    vUnion([vNumber(), vString()]),
    vTransform(v => v.toString()),
  )

export const vContToken = () => vOptional(vString())

export const vForward = forward

export function dangerouslyGetEmptyValue<T extends GenericSchema>(
  schema: T,
): any {
  if (schema === null || schema === undefined) return undefined

  const defaultSchemaVal = getDefault(schema)
  if (defaultSchemaVal !== undefined) return defaultSchemaVal

  // Handle custom vObject
  if ('type' in schema && schema.type === 'object' && 'entries' in schema) {
    const result: Record<string, any> = {}
    for (const [key, value] of Object.entries(
      schema.entries as Record<string, GenericSchema>,
    )) {
      result[key] = dangerouslyGetEmptyValue(value)
    }
    return result
  }

  if ('type' in schema && schema.type === 'nullish' && 'wrapped' in schema) {
    return dangerouslyGetEmptyValue(schema.wrapped as GenericSchema)
  }

  if ('type' in schema) {
    switch (schema.type) {
      case 'string':
        return ''
      case 'number':
        return 0
      case 'boolean':
        return false
      case 'array':
        return []
      case 'null':
        return null
    }
  }

  return ''
}
